import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap } from '@cm/core/localization';

const dpRoot = `Dashboard`;

export type HomeDashboardLoc =
	| 'SPHomeDashboardContentWelcome'
	| 'SPHomeDashboardExamWidgetTitle'
	| 'SPHomeDashboardExamWidgetCTA'
	| 'SPHomeDashboardCReqsWidgetTitle'
	| 'SPHomeDashboardCReqsWidgetCTA'
	| 'SPHomeDashboardMergeWidgetTitle'
	| 'SPHomeDashboardMergeWidgetCTA'
	| 'SPHomeDashboardVoucherCountWidgetTitle'
	| 'SPHomeDashboardVoucherCountWidgetCTA'
	| 'SPHomeDashboardAccommodationsWidgetTitle'
	| 'SPHomeDashboardAccommodationsWidgetCTA'
	| 'SPHomeDashboardTitlePage'
	| 'SPHomeDashboardAuditWidgetTitle'
	| 'SPHomeDashboardAuditWidgetCTA'
	| 'SPHomeDashboardNcarbReportsWidgetTitle'
	| 'SPHomeDashboardNcarbReportsWidgetCTA';

export const homeDashboardLocs: LocDefs<HomeDashboardLoc> = {
	SPHomeDashboardContentWelcome: {
		defaultValue: 'Welcome to CertMetrics',
		description: 'First item displayed on the dashboard.',
		displayPath: `${dpRoot}|Welcome Message`,
		displayType: 'Content',
		htmlAllowed: true,
		feature: HubFeature.Core,
		portal: 'SP',
	},
	SPHomeDashboardExamWidgetTitle: {
		defaultValue: 'Exams Pending Review',
		description: 'Title for the exams pending widget on the dashboard.',
		displayPath: `${dpRoot}|Exam Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.ExamSecurity,
		portal: 'SP',
		legacyName: 'Exam.Security/Home.Dashboard.Title.ExamWidgetTitle',
	},
	SPHomeDashboardExamWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the exams pending widget on the dashboard.',
		displayPath: `${dpRoot}|Exam Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.ExamSecurity,
		portal: 'SP',
		legacyName: 'Exam.Security/Home.Dashboard.Button.ExamWidgetCTA',
	},
	SPHomeDashboardCReqsWidgetTitle: {
		defaultValue: 'Requirements Pending Review',
		description: 'Title for the merge requests widget on the dashboard.',
		displayPath: `${dpRoot}|Requirements Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.OtherRequirement,
		portal: 'SP',
		legacyName: 'OtherRequirement/Home.Dashboard.Title.CReqsWidgetTitle',
	},
	SPHomeDashboardCReqsWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the merge requests widget on the dashboard.',
		displayPath: `${dpRoot}|Requirements Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.OtherRequirement,
		portal: 'SP',
		legacyName: 'OtherRequirement/Home.Dashboard.Button.CReqsWidgetCTA',
	},
	SPHomeDashboardMergeWidgetTitle: {
		defaultValue: 'Merge Requests',
		description: 'Title for the merge widget on the dashboard.',
		displayPath: `${dpRoot}|Merge Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.MergeCandidates,
		portal: 'SP',
		legacyName: 'MergeCandidates/Home.Dashboard.Title.MergeWidgetTitle',
	},
	SPHomeDashboardMergeWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the requirements pending widget on the dashboard.',
		displayPath: `${dpRoot}|Merge Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.MergeCandidates,
		portal: 'SP',
		legacyName: 'MergeCandidates/Home.Dashboard.Button.MergeWidgetCTA',
	},
	SPHomeDashboardVoucherCountWidgetTitle: {
		defaultValue: 'Benefits with low voucher counts',
		description: 'Title for the vouchers widget on the dashboard.',
		displayPath: `${dpRoot}|Voucher Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.Benefit,
		portal: 'SP',
		legacyName: 'Benefit/Home.Dashboard.Title.VoucherCountWidgetTitle',
	},
	SPHomeDashboardVoucherCountWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the for the vouchers widget on the dashboard.',
		displayPath: `${dpRoot}|Voucher Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.Benefit,
		portal: 'SP',
		legacyName: 'Benefit/Home.Dashboard.Button.VoucherCountWidgetCTA',
	},
	SPHomeDashboardTitlePage: {
		defaultValue: 'Dashboard',
		description: 'Page title.',
		displayPath: `${dpRoot}|Page title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.Core,
		portal: 'SP',
		legacyName: 'Core/Home.Dashboard.Title.PageTitle',
	},
	SPHomeDashboardAccommodationsWidgetTitle: {
		defaultValue: 'Pending Accommodations',
		description: 'Title for the pending accommodations widget on the dashboard.',
		displayPath: `${dpRoot}|Pending Accommodations Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.ExamAccommodationsShowAccommWidget,
		portal: 'SP',
	},
	SPHomeDashboardAccommodationsWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the pending accommodations widget on the dashboard.',
		displayPath: `${dpRoot}|Pending Accommodations Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.ExamAccommodationsShowAccommWidget,
		portal: 'SP',
	},
	SPHomeDashboardAuditWidgetTitle: {
		defaultValue: 'Audits Assigned to Me',
		description: 'Title for the audit candidates widget on the dashboard.',
		displayPath: `${dpRoot}|Audit Candidates Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.OtherRequirementUploadCandidateAccessAudits,
		portal: 'SP',
	},
	SPHomeDashboardAuditWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the audit candidates widget on the dashboard.',
		displayPath: `${dpRoot}|Audit Candidates Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.OtherRequirementUploadCandidateAccessAudits,
		portal: 'SP',
	},
	SPHomeDashboardNcarbReportsWidgetTitle: {
		defaultValue: 'Candidate Needing Eligibilities',
		description: 'Title for the ncarb reports widget on the dashboard.',
		displayPath: `${dpRoot}|Ncarb Reports Widget Title`,
		displayType: 'Title',
		htmlAllowed: false,
		feature: HubFeature.NcarbReports,
		portal: 'SP',
	},
	SPHomeDashboardNcarbReportsWidgetCTA: {
		defaultValue: 'View',
		description: 'Call to action for the Ncarb Reports Count widget on the dashboard.',
		displayPath: `${dpRoot}|Ncarb Reports Count Widget CTA`,
		displayType: 'Content',
		htmlAllowed: false,
		feature: HubFeature.NcarbReports,
		portal: 'SP',
	},
};

export type HomeDashboardLocType = LocMap<HomeDashboardLoc>;
